import ErrorDialog from 'components/ErrorDialog/ErrorDialog';
import { useSessionContext } from 'context/SessionProvider/SessionProvider';
import { startOfDay } from 'date-fns';
import PublisherContractForm, { IPublisherContractValues } from 'features/inventory/publisher/components/PublisherContractForm/PublisherContractForm';
import PublisherContractFormValidation from 'features/inventory/publisher/validations/publisherContracts';
import { Formik, FormikHelpers as FormikActions } from 'formik';
import useError from 'hooks/Error/useError';
import {
  CreatePublisherContractLegacyMutationFn,
  PublisherLegacy,
  useCreatePublisherContractLegacyMutation,
} from 'interfaces/generated.types';
import pickBy from 'lodash/pickBy';
import React from 'react';
import { getContractTerritory, getContractTimezone } from 'utils/contracts';
import dateUtils from 'utils/date';
import { getCustomCurrencyBasedOnTerritory } from 'utils/defaultsByTerritory';
import { formatValues, handleFormErrors } from 'utils/forms';

export interface ICreatePublisherContractContainerProps {
  publisher: PublisherLegacy;
  handleClose: () => void;
  refetch: () => void;
  hasEditPermissions?: boolean;
}

export const submitCreatePublisherContract =
  ({
    createPublisherContract,
    toggleErrorModal,
  }: {
    createPublisherContract: CreatePublisherContractLegacyMutationFn;
    toggleErrorModal: () => void;
  }) =>
  (
    formValues: IPublisherContractValues,
    { setStatus, setSubmitting }: FormikActions<IPublisherContractValues>
  ) => {
    const formattedValues = formatValues(formValues);

    const filteredValues: any = pickBy(
      {
        ...formattedValues,
        publisherId: formValues.publisherId,
        currency: formValues.currency,
        territories: formValues.territories,
        targetingGroups: null,
        targetingMasterTemplates: null,
        timeZone: null,
        entityPermissions: null,
        notes: formValues.notes,
      },
      (value) => value === 0 || !!value
    );

    createPublisherContract({
      variables: filteredValues,
    }).catch((error) =>
      handleFormErrors({
        error,
        toggleErrorModal,
        setStatus,
        setSubmitting,
      })
    );
  };

const CreatePublisherContractContainer = ({
  publisher,
  handleClose,
  refetch,
  hasEditPermissions,
}: ICreatePublisherContractContainerProps) => {
  const {
    state: {
      user: { activeTerritory },
    },
  } = useSessionContext();

  const [createPublisherContract] = useCreatePublisherContractLegacyMutation({
    onCompleted() {
      refetch();
      handleClose();
    },
  });

  const { hasError, toggleErrorModal, errorMessages } = useError([
    "Something went wrong and we couldn't create the Contract.",
    'Would you like to go to Home or close the alert and try again?',
  ]);

  const timeZone = getContractTimezone(publisher, activeTerritory!);
  const contractTerritory = getContractTerritory(publisher, activeTerritory!);

  const initialValues: IPublisherContractValues = {
    id: '',
    publisherId: publisher.id,
    startDate: dateUtils.getDateInSpecificTimezone(
      startOfDay(new Date()).toISOString(),
      timeZone
    ),
    endDate: null as any,
    timeZone,
    publisherCategories: [],
    currency: getCustomCurrencyBasedOnTerritory(activeTerritory!),
    directSpotCpm: '',
    programmaticSpotCpm: '',
    spotRevenueShare: '',
    hostReadRevenueShare: '',
    hostReadCpm: '',
    sponsorshipRevenueShare: '',
    minInventoryGuaranteed: '',
    maxInventoryGuaranteed: '',
    percentFillGuaranteed: '',
    minRevenueGuaranteed: '',
    minRevenueIdeal: '',
    territories: publisher.territories,
    notes: '',
  };

  return (
    <>
      <Formik<IPublisherContractValues>
        initialValues={initialValues}
        onSubmit={submitCreatePublisherContract({
          createPublisherContract,
          toggleErrorModal,
        })}
        validationSchema={PublisherContractFormValidation(contractTerritory)}
      >
        {(props) => (
          <PublisherContractForm
            {...props}
            handleClose={handleClose}
            testId="createPublisherContractForm"
            hasEditPermissions={hasEditPermissions}
          />
        )}
      </Formik>
      <ErrorDialog
        content={{
          title: 'Error',
          closeButton: 'Close',
          continueButton: 'Go to Home',
        }}
        handleContinue={() => handleClose()}
        isOpen={hasError}
        handleClose={toggleErrorModal}
        dataTc="createPublisherContractError"
        errorMessages={errorMessages}
      />
    </>
  );
};

export default CreatePublisherContractContainer;
